import type { FlaggedKeyword } from "@verity/database/prisma";
import { useCallback, useMemo, useState } from "react";
import { type VerityApi } from "../../api/fetch";
import { CoreModal } from "../../core";
import { FlaggedKeywordsTable, type FlaggedKeywordWithInstituteLevel } from "../../settings/flaggedKeywords";
import { useInstituteKeywords } from "../../settings/hooks";
import i18nPackage from "../../i18n/i18n";

export default function FlaggedKeywordsModal({
	onClose,
	instituteUuid,
	optionKeywords,
	onSaveOptionKeywords,
	getInstituteSettings,
}: {
	onClose: () => void;
	instituteUuid?: string;
	optionKeywords: FlaggedKeyword[];
	onSaveOptionKeywords: (keywords: FlaggedKeyword[]) => void;
	getInstituteSettings?: VerityApi["getInstituteSettings"];
}) {
	const { instituteKeywords, loading } = useInstituteKeywords(getInstituteSettings, instituteUuid);
	const [currentOptionKeywords, setCurrentOptionKeywords] = useState<FlaggedKeyword[]>(optionKeywords);
	const [saving, setSaving] = useState(false);

	const handleSave = useCallback(() => {
		setSaving(true);
		try {
			onSaveOptionKeywords(currentOptionKeywords);
			onClose();
		} catch (error) {
			console.error("Failed to save keywords:", error);
		} finally {
			setSaving(false);
		}
	}, [currentOptionKeywords, onClose, onSaveOptionKeywords]);

	const handleKeywordChange = useCallback((keywords: FlaggedKeywordWithInstituteLevel[]) => {
		const editableKeywords = keywords.filter((kw) => !kw.isInstituteLevel);
		setCurrentOptionKeywords(editableKeywords);
	}, []);

	const displayKeywords = useMemo((): FlaggedKeywordWithInstituteLevel[] => {
		const markedKeywords = instituteKeywords.map((kw) => ({ ...kw, isInstituteLevel: true }));

		return [...currentOptionKeywords, ...markedKeywords];
	}, [instituteKeywords, currentOptionKeywords]);

	return (
		<CoreModal
			open
			onClose={onClose}
			title={i18nPackage.t("settings.flaggedKeywords.title")}
			minWidth="min(90%, 800px)"
			minHeight="min(80%, 600px)"
			widthFixed
			heightFixed
			disableTopCloseButton={saving}
			buttons={[
				{
					text: i18nPackage.t("settings.flaggedKeywords.cancel"),
					color: "secondary",
					onClick: onClose,
					disabled: saving,
				},
				{
					text: i18nPackage.t("settings.flaggedKeywords.save"),
					color: "primary",
					onClick: handleSave,
					disabled: saving,
				},
			]}
		>
			<FlaggedKeywordsTable
				viewingContext="quiz"
				keywords={displayKeywords}
				onSettingChange={handleKeywordChange}
				loading={loading}
			/>
		</CoreModal>
	);
}
